// GalleryPinchZoom must be declared before customElements.define() below:
// defining the element synchronously upgrades any <media-gallery> already in the
// DOM, whose constructor calls initInlineZoom() -> new GalleryPinchZoom(). Keeping
// the class first avoids a temporal-dead-zone ReferenceError on that upgrade.
/*
 * GalleryPinchZoom — dependency-free inline zoom for the product media gallery.
 * Two-finger pinch scales the touched image; one-finger drag pans while zoomed;
 * releasing near 1x snaps back. Single-finger swipe (not zoomed) and single tap
 * (opens the lightbox modal) are left untouched so the existing gallery UX still
 * works. Zoom resets whenever the active slide changes.
 */
class GalleryPinchZoom {
  constructor(gallery, list) {
    this.gallery = gallery;
    this.list = list;
    this.img = null;
    this.item = null;
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.startDist = 0;
    this.startScale = 1;
    this.panStartX = 0;
    this.panStartY = 0;
    this.panStartTx = 0;
    this.panStartTy = 0;
    this.mode = null; // 'pinch' | 'pan' | null

    const opts = { passive: false };
    list.addEventListener('touchstart', this.onTouchStart.bind(this), opts);
    list.addEventListener('touchmove', this.onTouchMove.bind(this), opts);
    list.addEventListener('touchend', this.onTouchEnd.bind(this), opts);
    list.addEventListener('touchcancel', this.onTouchEnd.bind(this), opts);
    // Suppress Safari's native page pinch-zoom over the gallery.
    list.addEventListener('gesturestart', (event) => event.preventDefault());
    // Any swipe / thumbnail change resets the zoom state.
    if (gallery.elements.viewer) {
      gallery.elements.viewer.addEventListener('slideChanged', () => this.reset());
    }
  }

  imageFrom(target) {
    const item = target.closest && target.closest('.product__media-item');
    if (!item || item.classList.contains('product__media-item--full')) return null;
    return item.querySelector('.product__modal-opener--image img');
  }

  setImage(img) {
    if (this.img === img) return;
    if (this.img) this.reset();
    this.img = img;
    this.item = img.closest('.product__media-item');
  }

  onTouchStart(event) {
    if (event.touches.length === 2) {
      const img = this.imageFrom(event.target);
      if (!img) return;
      this.setImage(img);
      this.mode = 'pinch';
      this.startDist = this.distance(event.touches);
      this.startScale = this.scale;
      this.img.style.transition = 'none';
      event.preventDefault();
      return;
    }

    if (event.touches.length === 1 && this.img && this.scale > 1) {
      // Already zoomed: single finger pans (and blocks the modal from opening).
      const touch = event.touches[0];
      this.mode = 'pan';
      this.panStartX = touch.clientX;
      this.panStartY = touch.clientY;
      this.panStartTx = this.tx;
      this.panStartTy = this.ty;
      this.img.style.transition = 'none';
      event.preventDefault();
    }
  }

  onTouchMove(event) {
    if (this.mode === 'pinch' && event.touches.length === 2) {
      event.preventDefault();
      const next = this.startScale * (this.distance(event.touches) / this.startDist);
      this.scale = Math.min(Math.max(next, 1), 4);
      this.clamp();
      this.apply();
    } else if (this.mode === 'pan' && event.touches.length === 1) {
      event.preventDefault();
      const touch = event.touches[0];
      this.tx = this.panStartTx + (touch.clientX - this.panStartX);
      this.ty = this.panStartTy + (touch.clientY - this.panStartY);
      this.clamp();
      this.apply();
    }
  }

  onTouchEnd(event) {
    if (event.touches.length > 0) return;
    if (!this.img) {
      this.mode = null;
      return;
    }
    this.img.style.transition = '';
    if (this.scale <= 1.05) {
      this.reset();
    } else {
      if (this.item) this.item.classList.add('is-zoomed');
      this.clamp();
      this.apply();
    }
    this.mode = null;
  }

  distance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy) || 1;
  }

  clamp() {
    if (!this.img) return;
    const maxX = Math.max(0, (this.img.clientWidth * this.scale - this.img.clientWidth) / 2);
    const maxY = Math.max(0, (this.img.clientHeight * this.scale - this.img.clientHeight) / 2);
    this.tx = Math.min(Math.max(this.tx, -maxX), maxX);
    this.ty = Math.min(Math.max(this.ty, -maxY), maxY);
  }

  apply() {
    this.img.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
  }

  reset() {
    if (!this.img) {
      this.mode = null;
      return;
    }
    const img = this.img;
    const item = this.item;
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.mode = null;
    this.img = null;
    this.item = null;
    img.style.transition = 'transform 0.25s ease';
    img.style.transform = '';
    if (item) item.classList.remove('is-zoomed');
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Inline pinch-zoom / pan on the main image (touch devices only).
        // Runs before the no-thumbnail early-return so single-media products zoom too.
        this.initInlineZoom();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        // Use rAF to defer layout reads until after the browser has painted
        requestAnimationFrame(() => {
          if (this.elements.thumbnails) {
            const scroller = activeMedia.parentElement;
            // On mobile the main media is a horizontal scroll-snap slider. iOS
            // Safari (and `scroll-snap-stop: always`) refuses a programmatic
            // scroll that jumps across snap points, so tapping a thumbnail would
            // flag the slide active but leave the old image on screen. Disable
            // snapping for the jump, then restore it once the scroll has landed.
            const prevSnap = scroller.style.scrollSnapType;
            scroller.style.scrollSnapType = 'none';
            scroller.scrollTo({ left: activeMedia.offsetLeft });
            requestAnimationFrame(() => {
              scroller.style.scrollSnapType = prevSnap;
            });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        // Batch layout reads before DOM writes to avoid forced reflow
        const isVisible = this.elements.thumbnails.isSlideVisible(thumbnail, 10);
        const scrollTarget = thumbnail.offsetLeft;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (isVisible) return;

        this.elements.thumbnails.slider.scrollTo({ left: scrollTarget });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initInlineZoom() {
        // Only on coarse-pointer / no-hover devices (phones, tablets). Desktop keeps
        // its hover / lightbox-modal zoom untouched.
        if (!window.matchMedia('(hover: none) and (pointer: coarse)').matches) return;
        const list = this.elements.viewer && this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        if (!list) return;
        this.inlineZoom = new GalleryPinchZoom(this, list);
      }
    }
  );
}
